/**
 * Custom PostHog Context
 *
 * Provides PostHog instance to the app without static imports from @posthog/react.
 * This enables true deferred loading - the PostHog SDK is only downloaded when
 * DeferredPostHogProvider loads it dynamically.
 *
 * Why not use @posthog/react directly?
 * - Importing `usePostHog` from @posthog/react adds ~150KB+ to the initial bundle
 * - Even if you defer initialization, the code is still downloaded and parsed
 * - This custom context avoids that by only dynamically importing PostHog when needed
 *
 * Usage:
 * - Components use `usePostHog()` from this file (not from @posthog/react)
 * - Returns PostHog | undefined (undefined until SDK loads)
 * - All existing null checks (if (posthog) ...) continue to work
 */
import * as React from 'react'
import type { PostHog } from 'posthog-js'

// Context holds the PostHog instance (undefined until loaded)
const PostHogContext = React.createContext<PostHog | undefined>(undefined)

interface PostHogContextProviderProps {
  children: React.ReactNode
  client: PostHog | undefined
}

/**
 * Provider component used internally by DeferredPostHogProvider.
 * Not exported - use DeferredPostHogProvider instead.
 */
export function PostHogContextProvider({ children, client }: PostHogContextProviderProps) {
  return <PostHogContext.Provider value={client}>{children}</PostHogContext.Provider>
}

/**
 * Hook to access the PostHog instance.
 *
 * Returns undefined if:
 * - PostHog hasn't loaded yet (deferred loading)
 * - PostHog is blocked by an adblocker
 * - App is running in development without PostHog enabled
 *
 * Always check if posthog is defined before calling methods:
 * ```ts
 * const posthog = usePostHog()
 * if (posthog) {
 *   posthog.capture('event_name')
 * }
 * ```
 *
 * Or use the safeCapture helper from capture.ts which handles this for you.
 */
export function usePostHog(): PostHog | undefined {
  return React.useContext(PostHogContext)
}
